import Component from '@glimmer/component';
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt({
  html: true,
  breaks: true,
  linkify: true,
  typographer: true,
});

// Lines that are already block-level markdown — their spacing/structure is
// intentional, so the normalizer leaves them untouched.
const STRUCTURAL =
  /^\s*(#{1,6}\s|>|\d+[.)]\s|[-*+]\s|\||={3,}\s*$|-{3,}\s*$|\s{4}|\t)/;
const FENCE = /^\s*(```|~~~)/;

// A standalone, short, title-cased line with no terminal sentence punctuation
// reads as a section heading, not a sentence. Authored article bodies often
// arrive as plain text where section labels are bare lines (no `##`), which
// makes them render identically to body copy. Promote those to real headings.
function looksLikeHeading(line, next) {
  const t = (line || '').trim();
  if (!t || t.length > 60) return false;
  if (STRUCTURAL.test(line) || FENCE.test(t)) return false;
  if (/[.,;:!?]$/.test(t)) return false; // headings don't end in sentence punctuation
  if (/[.!?]\s/.test(t)) return false; // contains a sentence break → it's prose
  if (!/^[A-Z0-9"'(]/.test(t)) return false; // starts strong
  if (t.split(/\s+/).length > 9) return false; // headings are short
  // A following `===`/`---` line means this is a setext heading (which
  // markdown-it already renders) or a horizontal rule — promoting it would
  // leave the underline behind as a stray paragraph, so leave it untouched.
  if (next && /^\s*(={3,}|-{3,})\s*$/.test(next)) return false;
  return !!next && next.trim().length > 0; // followed by a body line
}

// Restructure plain-text article bodies into proper markdown blocks: give every
// hard-wrapped line its own paragraph (so the body isn't one dense wall of
// <br>-joined text) and promote bare heading-like lines to `##`. Existing
// markdown structure — lists, code fences, blockquotes, tables, real headings —
// is preserved as-is, so this is a no-op for already-well-formed content.
function structureBody(src) {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let inFence = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const t = line.trim();

    if (FENCE.test(t)) {
      inFence = !inFence;
      out.push(line);
      continue;
    }
    if (inFence) {
      out.push(line);
      continue;
    }

    const next = lines[i + 1];

    if (t && looksLikeHeading(line, next)) {
      if (out.length && out[out.length - 1].trim() !== '') out.push('');
      out.push(`## ${t}`);
      out.push('');
      continue;
    }

    out.push(line);

    // Separate two adjacent plain-text lines into distinct paragraphs.
    if (next !== undefined) {
      const nt = next.trim();
      const bothProse =
        t !== '' &&
        nt !== '' &&
        !STRUCTURAL.test(line) &&
        !STRUCTURAL.test(next) &&
        !FENCE.test(nt) &&
        !looksLikeHeading(next, lines[i + 2]);
      if (bothProse) out.push('');
    }
  }

  return out.join('\n');
}

export default class Markdown extends Component {
  get html() {
    if (!this.args.markdown) {
      return '';
    }

    let rendered = md.render(structureBody(this.args.markdown));
    
    // Apply styling classes to match the site's design
    rendered = rendered
      .replace(/<p>/g, '<p class="post-open-text">')
      .replace(/<h1>/g, '<h1 class="post-open-subtitle">')
      .replace(/<h2>/g, '<h2 class="post-open-subtitle">')
      .replace(/<h3>/g, '<h3 class="post-open-subtitle">')
      .replace(/<h4>/g, '<h4 class="post-open-subtitle">')
      .replace(/<h5>/g, '<h5 class="post-open-subtitle">')
      .replace(/<h6>/g, '<h6 class="post-open-subtitle">')
      .replace(/<blockquote>/g, '<div class="post-open-quote"><blockquote class="quote-text">')
      .replace(/<\/blockquote>/g, '</blockquote></div>')
      .replace(/<ul>/g, '<div class="bullet-list bullet-list-grid"><ul>')
      .replace(/<\/ul>/g, '</ul></div>')
      .replace(/<li>/g, '<li class="bullet-list-item"><div class="bullet-list-item-icon"><svg class="check-icon"><use href="#svg-check"></use></svg></div><p class="bullet-list-item-text">')
      .replace(/<\/li>/g, '</p></li>')
      .replace(/<ol>/g, '<div class="bullet-list bullet-list-grid"><ol>')
      .replace(/<\/ol>/g, '</ol></div>')
      .replace(/<pre>/g, '<pre class="post-code-block">')
      .replace(/<code>/g, '<code class="post-code">')
      .replace(/<strong>/g, '<strong>')
      .replace(/<em>/g, '<em>')
      .replace(/<a href="([^"]*)"/g, '<a href="$1" class="post-link"');

    return rendered;
  }

  <template>
    <div class="post-content-formatted">
      {{{this.html}}}
    </div>
  </template>
}
